import type { Collection, CollectionRelation } from '@rstore/shared';
import type { CustomCollectionMeta } from '#layers/data-model/types/rstore';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { pick } from '#layers/base/public';
import { stringifyStableId } from '#layers/data-model/utils/primary-key';
import { getRelationPrimitiveFields } from '#layers/data-model/utils/relation';
import { getCollectionPrimaryKeyFieldNames } from '#layers/data-model/utils/schema/collection';
import { resolveFieldParser } from '#layers/data-model/utils/schema/primitive-field-parsers';
import { getTypename, isPrimitiveTypeUnsupported } from '#layers/data-model/utils/schema/primitive-type';

/** Scope shared by every collection backed by the workspace schema, matching the monospace rstore plugin. */
export const MONOSPACE_SCOPE_ID = 'monospace-default';

/**
 * Turns a workspace schema collection into the rstore collection definition the app queries against.
 *
 * Pure: it neither reads nor registers anything on the store, so the caller decides whether to add
 * the definition or patch an existing collection with its parts.
 *
 * System collections keep their hand-written key and relations — they are declared in
 * `app/rstore/schema` and only borrow the metadata derived from the schema.
 */
export function toStoreCollection(monospaceCollection: ResolvedMonospaceCollection) {
	const fields = Object.fromEntries(
		monospaceCollection.primitiveFields
			.map((field) => (
				[field.apiName, resolveFieldParser(getTypename(field.type))]
			))
			.filter(([_, parser]) => !!parser),
	);

	const primitiveFieldTypes: Record<string, PrimitiveType> = Object.fromEntries(
		monospaceCollection.primitiveFields.map((field) => [field.apiName, field.type]),
	);

	const relationsWithConstraint = new Set<string>();

	for (const relationField of monospaceCollection.relationFields) {
		// Checking if the relation field has a constraint
		// should be enough to determine if it is the side
		// that contains the primitive field of the relation
		if (relationField.constraintId) {
			relationsWithConstraint.add(relationField.apiName);
		}
	}

	const meta: CustomCollectionMeta = {
		defaultFields: monospaceCollection.primitiveFields
			.filter((field) => !isPrimitiveTypeUnsupported(field.type))
			.map((field) => field.apiName),
		fromWorkspaceCollections: true,
		relationsWithConstraint,
		primitiveFieldTypes,
		// A declared contract can narrow what a mutation returns; `*` asks for exactly that output.
		...(monospaceCollection.isSystem ? {} : { mutationFields: ['*'] }),
	};

	let getKey: ((item: AnyItem) => string) | undefined;

	if (!monospaceCollection.isSystem) {
		// Only user collections are served by `/items`; system ones declare their endpoint statically.
		meta.useItemsEndpoint = true;
		const primaryKeyFieldNames = getCollectionPrimaryKeyFieldNames(monospaceCollection);
		meta.isKeyComposite = primaryKeyFieldNames.length > 1;
		getKey = createStoreCollectionGetKey(primaryKeyFieldNames);
	}

	// `defineCollection` is deliberately not used here: it is typed as `(c: Collection) => Collection`,
	// so it would widen `meta` and `relations` back to optional for the callers that patch with them.
	return {
		name: monospaceCollection.apiName,
		fields,
		scopeId: MONOSPACE_SCOPE_ID,
		relations: buildRelations(monospaceCollection),
		getKey,
		meta,
	} satisfies Collection;
}

/**
 * Key derivation shared by every workspace collection: the single primary key value, or the
 * stable JSON of every primary key field for a composite key.
 *
 * The composite form stringifies whichever key fields the item carries, so a partial key still
 * yields a truthy, wrong key. Callers keying rows they did not fetch (cache writes built from a
 * mutation payload) must check the key is complete first.
 */
export function createStoreCollectionGetKey(primaryKeyFieldNames: string[]): (item: AnyItem) => string {
	if (primaryKeyFieldNames.length > 1) {
		return (item) => stringifyStableId(pick(item, primaryKeyFieldNames));
	}

	const [primaryKeyFieldName] = primaryKeyFieldNames;

	return (item) => item[primaryKeyFieldName!];
}

/**
 * Maps the schema relation fields to rstore relations, keyed by the primitive fields backing each side.
 *
 * A relation the schema can't resolve is reported and skipped rather than failing the whole
 * collection, which would leave the app with no way to read it at all.
 */
function buildRelations(monospaceCollection: ResolvedMonospaceCollection): Record<string, CollectionRelation> {
	const relations: Record<string, CollectionRelation> = {};

	if (monospaceCollection.isSystem) return relations;

	try {
		for (const relationField of monospaceCollection.relationFields) {
			const oppositeCollection = relationField.oppositeCollection;

			if (!oppositeCollection) {
				throw new Error(`Opposite collection ${relationField.oppositeCollectionId} not found for relation field ${relationField.apiName}`);
			}

			const { fields, oppositeFields } = getRelationPrimitiveFields(relationField);

			if (fields.length === 0) {
				throw new Error(`No primitive field found for relation field ${relationField.apiName}`);
			}

			if (oppositeFields.length === 0) {
				throw new Error(`No primitive field found for opposite relation field ${relationField.apiName}`);
			}

			relations[relationField.apiName] = {
				many: relationField.isList,
				to: {
					[oppositeCollection.apiName]: {
						on: Object.fromEntries(
							oppositeFields
								.map((oppositeField, index) => [oppositeField.apiName, fields[index]!.apiName]),
						),
					},
				},
			};
		}
	}
	catch (error) {
		console.warn(`Failed to resolve relations for collection ${monospaceCollection.apiName}:`, error);
	}

	return relations;
}
